import React from 'react';
import { Empty, Table, Tag } from 'antd';
import styles from '@pages/api-portal/styles.module.scss';
import { BodyCodeBlock } from './body-code-block';
import { ApiEndpoint, Parameter } from '@pages/api-portal/types';

interface ParametersTableProps {
    endpoint: ApiEndpoint;
}

const PARAM_COLUMNS = (hasRequired: boolean) => {
    const columns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'Type', dataIndex: 'type', key: 'type' },
        { title: 'Description', dataIndex: 'description', key: 'description' },
        ...(hasRequired
            ? [
                  {
                      title: 'Required',
                      dataIndex: 'required',
                      key: 'required',
                      render: (required: boolean) => (required ? <Tag color="green">Yes</Tag> : null),
                  },
              ]
            : []),
        { title: 'Example', dataIndex: 'example', key: 'example', render: (v: string) => v || '-' },
    ];
    return columns;
};

function ParamSection({ title, params }: { title: string; params: Parameter[] }) {
    const hasRequired = params.some((p) => p.required);
    return (
        <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <h5 style={{ margin: 0, color: '#fff' }}>{title}</h5>
                <Tag>{params.length} item{params.length !== 1 ? 's' : ''}</Tag>
            </div>
            <Table
                className={styles.paramsTable}
                size="small"
                rowKey="name"
                pagination={false}
                columns={PARAM_COLUMNS(hasRequired)}
                dataSource={params}
            />
        </div>
    );
}

/*
 * Renders path/query/header/body documentation for a catalog endpoint's detail page.
 */
export function ParametersTable({ endpoint }: ParametersTableProps) {
    const { path, query, header, body } = endpoint.parameters ?? {};
    const hasParameters = path?.length || query?.length || header?.length || body;

    if (!hasParameters) {
        return <Empty description="This endpoint does not require any path, query, header, or body parameters." />;
    }

    return (
        <div>
            {path && path.length > 0 && <ParamSection title="Path Parameters" params={path} />}
            {query && query.length > 0 && <ParamSection title="Query Parameters" params={query} />}
            {header && header.length > 0 && <ParamSection title="Headers" params={header} />}
            {body && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <h5 style={{ margin: 0, color: '#fff' }}>Request Body</h5>
                        <span className={styles.mutedText} style={{ fontSize: 12 }}>
                            {body.contentType}
                        </span>
                    </div>
                    <BodyCodeBlock label="Schema" data={body.schema} />
                    <div style={{ height: 8 }} />
                    <BodyCodeBlock label="Example" data={body.example} />
                </div>
            )}
        </div>
    );
}
